import { useState, useEffect, useRef, useCallback } from "react";
import * as React from "react";
import { QRCodeSVG } from "qrcode.react";
import PhotoPicker from "../components/PhotoPicker";
import QueueTab from "./QueueTab";
import AppointmentsTab from "./AppointmentsTab";
import DesignsTab from "./DesignsTab";
import WishlistsTab from "./WishlistsTab";
import WorkOrdersTab from "./WorkOrdersTab";
import TailorWorkOrdersTab from "./TailorWorkOrdersTab";
import RewardsTab from "./RewardsTab";
import JewelleryWorkOrdersTab from "./JewelleryWorkOrdersTab";
import CustomersTab from "./CustomersTab";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

const ITEM_CATEGORIES = [
  "All", "Snacks", "Sweets", "Drinks", "Bakery", "Dairy",
  "Grocery", "Fruits & Veg", "Clothing", "Electronics",
  "Jewellery", "Home Decor", "Other"
];

export default function OwnerDashboard() {
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState("catalogue");
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Wishlist badge
  const [newWishlistCount, setNewWishlistCount] = useState(0);

  // Orders
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersMode, setOrdersMode] = useState("live");
  const ordersIntervalRef = useRef(null);
  const [ordersNextKey, setOrdersNextKey] = useState(null);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Features
  const [features, setFeatures] = useState(null);
  const [savingFeatures, setSavingFeatures] = useState(false);

  // GST Profile
  const [gstProfile, setGstProfile] = useState({ gstin: "", legalName: "", address: "" });
  const [savingGST, setSavingGST] = useState(false);
  const [gstSaved, setGstSaved] = useState(false);

  // Booking Settings
  const [bookingSettings, setBookingSettings] = useState({ openTime: "09:00", closeTime: "20:00", slotMinutes: 30, maxPerSlot: 1 });
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [savingTheme, setSavingTheme] = useState(false);
  const [themeSaved, setThemeSaved] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);
  const [bookingSaved, setBookingSaved] = useState(false);

  // Reports
  const [reportOrders, setReportOrders] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportPeriod, setReportPeriod] = useState("week");

  // Shop media
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const coverRef = useRef(null);
  const logoRef = useRef(null);

  // Item form
  const emptyItem = { name: "", price: "", category: "Other", description: "", inStock: true, photoUrl: null, unit: "" };
  const [itemForm, setItemForm] = useState(emptyItem);

  // ── Auth gate ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const businessId = localStorage.getItem("qkard_businessId");
    const phone = localStorage.getItem("qkard_phone");
    if (!businessId || !phone) {
      window.location.replace("/login");
      return;
    }
    loadOwnerData(phone).finally(() => setLoading(false));
  }, []);

  // ── Load owner data ───────────────────────────────────────────────────────
  async function loadOwnerData(phone) {
    try {
      const res = await fetch(`${API}/shops?phone=${encodeURIComponent(phone)}`);
      const data = await res.json();
      if (data.business) {
        setBusiness(data.business);
        const cat = data.catalogues?.[0] || null;
        setCatalogue(cat);
        if (cat?.features) setFeatures(cat.features);
        if (cat?.features?.hasLookbook || data.business?.features?.hasLookbook) setActiveTab("designs");
        else setFeatures({ hasOrders: true, hasTables: false, hasWaiter: false, hasBooking: false, hasDelivery: false, hasQueue: false });
        // Load GST profile
        if (cat) {
          setGstProfile({
            gstin: cat.gstin || "",
            legalName: cat.legalName || "",
            address: cat.address || "",
          });
          // Load booking settings
          if (cat.theme) setSelectedTheme(cat.theme);
          else if (cat.accentColor) {
            // Find theme by accent color
            const found = THEMES.find(t => t.accent === cat.accentColor);
            if (found) setSelectedTheme(found.id);
          }
          if (cat.bookingSettings) {
            setBookingSettings({
              openTime: cat.bookingSettings.openTime || "09:00",
              closeTime: cat.bookingSettings.closeTime || "20:00",
              slotMinutes: cat.bookingSettings.slotMinutes || 30,
              maxPerSlot: cat.bookingSettings.maxPerSlot || 1,
            });
          }
          await loadItems(cat.catalogueId);
        }
      }
    } catch (e) {
      console.error("Failed to load owner data", e);
    }
  }

  async function loadItems(catalogueId) {
    try {
      const res = await fetch(`${API}/items?catalogueId=${catalogueId}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      console.error("Failed to load items", e);
    }
  }

  async function loadOrders(businessId, mode = "live", append = false) {
    if (append) setLoadingMore(true);
    else setOrdersLoading(true);
    try {
      let url = `${API}/orders?businessId=${businessId}&mode=${mode}`;
      if (mode === "history" && append && ordersNextKey) {
        url += `&lastKey=${encodeURIComponent(ordersNextKey)}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (mode === "history") {
        setOrders(prev => append ? [...prev, ...(data.orders || [])] : (data.orders || []));
        setOrdersNextKey(data.nextKey || null);
        setOrdersHasMore(data.hasMore || false);
      } else {
        setOrders(data.orders || []);
        setOrdersNextKey(null);
        setOrdersHasMore(false);
      }
    } catch (e) {
      console.error("Failed to load orders", e);
    } finally {
      setOrdersLoading(false);
      setLoadingMore(false);
    }
  }

  async function loadReportOrders(businessId, period = reportPeriod) {
    setReportLoading(true);
    try {
      // Calculate date range from period
      const to = new Date().toISOString().split("T")[0];
      const fromDate = new Date();
      if (period === "today") fromDate.setHours(0, 0, 0, 0);
      else if (period === "week") fromDate.setDate(fromDate.getDate() - 6);
      else if (period === "month") fromDate.setDate(fromDate.getDate() - 29);
      const from = fromDate.toISOString().split("T")[0];
      const res = await fetch(`${API}/orders?businessId=${businessId}&mode=report&from=${from}&to=${to}`);
      const data = await res.json();
      setReportOrders(data.orders || []);
    } catch (e) {
      console.error("Failed to load report orders", e);
    } finally {
      setReportLoading(false);
    }
  }

  // Auto-refresh orders every 10s when on orders tab (live mode only)
  useEffect(() => {
    if (activeTab === "orders" && business) {
      setOrders([]);
      setOrdersNextKey(null);
      loadOrders(business.businessId, ordersMode);
      if (ordersMode === "live") {
        ordersIntervalRef.current = setInterval(() => loadOrders(business.businessId, "live"), 10000);
      }
    } else {
      clearInterval(ordersIntervalRef.current);
    }
    return () => clearInterval(ordersIntervalRef.current);
  }, [activeTab, business, ordersMode]);

  useEffect(() => {
    if (activeTab === "reports" && business) {
      loadReportOrders(business.businessId, reportPeriod);
    }
  }, [activeTab, business, reportPeriod]);

  async function handleUpdateOrderStatus(orderId, status) {
    try {
      await fetch(`${API}/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      setOrders(prev => prev.map(o => o.orderId === orderId ? { ...o, status } : o));
    } catch (e) {
      alert("Could not update order.");
    }
  }

  const THEMES = [
    { id: "fresh-green",   name: "Fresh Green",    accent: "#22c55e", bg: "#f0fdf4", text: "#14532d", preview: ["#22c55e","#f0fdf4","#14532d"], emoji: "🌿" },
    { id: "ocean-blue",    name: "Ocean Blue",     accent: "#2563a8", bg: "#eff6ff", text: "#1e3a5f", preview: ["#2563a8","#eff6ff","#1e3a5f"], emoji: "🌊" },
    { id: "saffron-gold",  name: "Saffron Gold",   accent: "#f5a623", bg: "#fffbeb", text: "#78350f", preview: ["#f5a623","#fffbeb","#78350f"], emoji: "🌼" },
    { id: "dark-premium",  name: "Dark Premium",   accent: "#a78bfa", bg: "#1a1a2e", text: "#e2e8f0", preview: ["#a78bfa","#1a1a2e","#e2e8f0"], emoji: "💎" },
    { id: "rose-pink",     name: "Rose Pink",      accent: "#ec4899", bg: "#fdf2f8", text: "#831843", preview: ["#ec4899","#fdf2f8","#831843"], emoji: "🌸" },
    { id: "earthy-brown",  name: "Earthy Brown",   accent: "#92400e", bg: "#fdf8f3", text: "#451a03", preview: ["#92400e","#fdf8f3","#451a03"], emoji: "🪵" },
  ];

  async function handleSaveTheme(themeId) {
    const theme = THEMES.find(t => t.id === themeId);
    if (!theme) return;
    setSavingTheme(true);
    try {
      const res = await fetch(`${API}/shops/features`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          catalogueId: catalogue.catalogueId,
          theme: themeId,
          accentColor: theme.accent,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      setSelectedTheme(themeId);
      setThemeSaved(true);
      setTimeout(() => setThemeSaved(false), 2000);
    } catch { alert("Could not save theme."); }
    finally { setSavingTheme(false); }
  }

  async function handleSaveFeatures(newFeatures) {
    setSavingFeatures(true);
    try {
      const res = await fetch(`${API}/shops/features`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ catalogueId: catalogue.catalogueId, features: newFeatures }),
      });
      if (!res.ok) throw new Error("Save failed");
      setFeatures(newFeatures);
    } catch (e) {
      alert("Could not save features. Please try again.");
    } finally {
      setSavingFeatures(false);
    }
  }

  async function handleSaveGSTProfile() {
    setSavingGST(true);
    try {
      const res = await fetch(`${API}/shops/features`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          catalogueId: catalogue.catalogueId,
          gstin: gstProfile.gstin.trim() || null,
          legalName: gstProfile.legalName.trim() || null,
          address: gstProfile.address.trim() || null,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      setGstSaved(true);
      setTimeout(() => setGstSaved(false), 3000);
    } catch (e) {
      alert("Could not save GST profile. Please try again.");
    } finally {
      setSavingGST(false);
    }
  }

  async function handleSaveBookingSettings() {
    setSavingBooking(true);
    try {
      const res = await fetch(`${API}/shops/features`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          catalogueId: catalogue.catalogueId,
          bookingSettings: {
            openTime: bookingSettings.openTime,
            closeTime: bookingSettings.closeTime,
            slotMinutes: Number(bookingSettings.slotMinutes),
            maxPerSlot: Number(bookingSettings.maxPerSlot),
          },
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      setBookingSaved(true);
      setTimeout(() => setBookingSaved(false), 3000);
    } catch (e) {
      alert("Could not save booking settings.");
    } finally {
      setSavingBooking(false);
    }
  }

  async function handleGenerateInvoice(order) {
    try {
      const INVOICE_API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";
      const res = await fetch(`${INVOICE_API}/invoice/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: order.orderId,
          catalogueId: order.catalogueId || catalogue?.catalogueId,
          businessId: business.businessId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      // Open invoice in new tab
      window.open(`${INVOICE_API}/invoice/${data.invoiceId}`, "_blank");
    } catch (e) {
      alert("Could not generate invoice: " + e.message);
    }
  }

  // ── Upload cover/logo to S3 ───────────────────────────────────────────────
  async function handleMediaUpload(file, type) {
    if (!file || !catalogue) return;
    const isLogo = type === "logo";
    isLogo ? setUploadingLogo(true) : setUploadingCover(true);
    try {
      const compressed = await compressImage(file, isLogo ? 400 : 1200);
      const phone = localStorage.getItem("qkard_phone");
      const res = await fetch(`${API}/upload/presign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, businessId: business.businessId, productId: "qkard", fileType: "image/jpeg", fileExt: "jpg", itemId: `${type}-${catalogue.catalogueId}` }),
      });
      if (!res.ok) throw new Error("Upload failed");
      const { presignedUrl, publicUrl } = await res.json();
      await fetch(presignedUrl, { method: "PUT", headers: { "Content-Type": "image/jpeg" }, body: compressed });
      await fetch(`${API}/shops/features`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          catalogueId: catalogue.catalogueId,
          [isLogo ? "logo" : "coverPhoto"]: `${publicUrl}?t=${Date.now()}`,
        }),
      });
      setCatalogue(prev => ({ ...prev, [isLogo ? "logo" : "coverPhoto"]: `${publicUrl}?t=${Date.now()}` }));
    } catch (e) {
      alert(`Could not upload ${type}. Please try again.`);
    } finally {
      isLogo ? setUploadingLogo(false) : setUploadingCover(false);
    }
  }

  async function compressImage(file, maxSize = 800) {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) { height = Math.round(height * maxSize / width); width = maxSize; }
          else { width = Math.round(width * maxSize / height); height = maxSize; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        canvas.toBlob(resolve, "image/jpeg", 0.85);
      };
      img.src = url;
    });
  }

  // ── Save item ─────────────────────────────────────────────────────────────
  async function handleSaveItem() {
    if (!itemForm.name.trim()) { setError("Item name is required"); return; }
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...itemForm,
        catalogueId: catalogue.catalogueId,
        price: itemForm.price ? parseFloat(itemForm.price) : null,
        photoUrl: itemForm.photoUrl || null,
      };
      const url = editingItem ? `${API}/items/${editingItem.itemId}` : `${API}/items`;
      const res = await fetch(url, {
        method: editingItem ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await loadItems(catalogue.catalogueId);
      setShowAddItem(false);
      setEditingItem(null);
      setItemForm(emptyItem);
    } catch (e) {
      setError("Could not save item. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // ── Delete item ───────────────────────────────────────────────────────────
  async function handleDeleteItem(itemId) {
    if (!window.confirm("Delete this item?")) return;
    try {
      await fetch(`${API}/items`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId, catalogueId: catalogue.catalogueId }),
      });
      setItems(prev => prev.filter(i => i.itemId !== itemId));
    } catch (e) {
      alert("Could not delete item.");
    }
  }

  function openEdit(item) {
    setItemForm({
      name: item.name,
      price: item.price?.toString() || "",
      category: item.category || "Other",
      description: item.description || "",
      inStock: item.inStock !== false,
      photoUrl: item.photoUrl || null,
      unit: item.unit || "",
    });
    setEditingItem(item);
    setShowAddItem(true);
  }

  function handleSignOut() {
    localStorage.clear();
    window.location.replace("/login");
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const s = {
    page: { minHeight: "100vh", background: "#f5f5f3", fontFamily: "'DM Sans', 'Inter', sans-serif" },
    header: {
      background: "#fff",
      borderBottom: "1px solid #efefed",
      padding: "0 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      position: "sticky",
      top: 0,
      zIndex: 100,
      height: 56,
    },
    logo: { fontSize: 22, fontWeight: 800, color: "#111", letterSpacing: "-0.5px" },
    logoAccent: { color: "#FF6B35" },
    signOutBtn: {
      background: "none", border: "1px solid #e5e5e5",
      borderRadius: 8, padding: "6px 12px",
      fontSize: 13, color: "#666", cursor: "pointer",
    },
    body: { padding: "0 0 120px 0", maxWidth: 680, margin: "0 auto" },
    catalogueCard: {
      background: "#111",
      margin: "16px",
      borderRadius: 16,
      padding: 20,
      color: "#fff",
    },
    liveLabel: {
      display: "inline-block",
      background: "#22c55e",
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      padding: "3px 8px",
      borderRadius: 20,
      marginBottom: 8,
      letterSpacing: "0.5px",
    },
    shopName: { fontSize: 22, fontWeight: 800, marginBottom: 4, letterSpacing: "-0.5px" },
    shopMeta: { fontSize: 13, color: "#aaa", marginBottom: 16 },
    urlRow: {
      background: "rgba(255,255,255,0.08)",
      borderRadius: 10,
      padding: "10px 14px",
      fontSize: 13,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      marginBottom: 12,
      wordBreak: "break-all",
    },
    copyBtn: {
      background: "#fff", color: "#111",
      border: "none", borderRadius: 8,
      padding: "6px 12px", fontSize: 12,
      fontWeight: 600, cursor: "pointer",
      whiteSpace: "nowrap", flexShrink: 0,
    },
    actionRow: { display: "flex", gap: 8 },
    actionBtn: {
      flex: 1, padding: "10px",
      borderRadius: 10, border: "none",
      fontSize: 13, fontWeight: 600,
      cursor: "pointer",
    },
    tabs: {
      display: "flex",
      background: "#fff",
      borderBottom: "1px solid #efefed",
      padding: "0 16px",
      gap: 4,
      overflowX: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch",
    },
    tab: {
      padding: "14px 16px",
      fontSize: 14, fontWeight: 500,
      color: "#888", cursor: "pointer",
      borderBottom: "2px solid transparent",
      transition: "all 0.15s",
    },
    tabActive: { color: "#111", borderBottomColor: "#111" },
    sectionHeader: {
      display: "flex", alignItems: "center",
      justifyContent: "space-between",
      padding: "20px 16px 12px",
    },
    sectionTitle: { fontSize: 16, fontWeight: 700, color: "#111" },
    addBtn: {
      background: "#111", color: "#fff",
      border: "none", borderRadius: 10,
      padding: "8px 16px", fontSize: 13,
      fontWeight: 600, cursor: "pointer",
    },
    itemCard: {
      background: "#fff",
      margin: "0 16px 10px",
      borderRadius: 14,
      padding: "14px 16px",
      display: "flex",
      alignItems: "center",
      gap: 14,
    },
    itemInfo: { flex: 1 },
    itemName: { fontSize: 15, fontWeight: 600, color: "#111", marginBottom: 2 },
    itemMeta: { fontSize: 13, color: "#888" },
    itemPrice: { fontSize: 16, fontWeight: 700, color: "#111", textAlign: "right" },
    itemActions: { display: "flex", gap: 6 },
    editBtn: {
      background: "#f5f5f3", border: "none",
      borderRadius: 8, padding: "6px 10px",
      fontSize: 12, cursor: "pointer", color: "#444",
    },
    deleteBtn: {
      background: "#fff0f0", border: "none",
      borderRadius: 8, padding: "6px 10px",
      fontSize: 12, cursor: "pointer", color: "#cc0000",
    },
    emptyState: {
      textAlign: "center", padding: "48px 24px",
      color: "#aaa", fontSize: 14,
    },
    overlay: {
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.5)",
      zIndex: 200, display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
    },
    modal: {
      background: "#fff",
      borderRadius: "20px 20px 0 0",
      padding: "24px 20px 40px",
      width: "100%",
      maxWidth: 640,
      maxHeight: "90vh",
      overflowY: "auto",
    },
    modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 20 },
    label: { fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, display: "block" },
    input: {
      width: "100%", padding: "12px 14px",
      borderRadius: 10, border: "1.5px solid #e5e5e5",
      fontSize: 15, outline: "none", boxSizing: "border-box",
      marginBottom: 14,
    },
    select: {
      width: "100%", padding: "12px 14px",
      borderRadius: 10, border: "1.5px solid #e5e5e5",
      fontSize: 15, outline: "none", boxSizing: "border-box",
      background: "#fff", marginBottom: 14,
    },
    saveBtn: {
      width: "100%", padding: 14,
      borderRadius: 12, border: "none",
      background: "#111", color: "#fff",
      fontSize: 16, fontWeight: 600,
      cursor: "pointer", marginTop: 4,
    },
    cancelBtn: {
      width: "100%", padding: 14,
      borderRadius: 12, border: "1.5px solid #e5e5e5",
      background: "#fff", color: "#444",
      fontSize: 15, fontWeight: 500,
      cursor: "pointer", marginTop: 8,
    },
    stockRow: {
      display: "flex", alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 0", marginBottom: 14,
      borderTop: "1px solid #f0f0f0",
      borderBottom: "1px solid #f0f0f0",
    },
    toggle: {
      width: 44, height: 24,
      borderRadius: 12,
      border: "none", cursor: "pointer",
      position: "relative",
      transition: "background 0.2s",
    },
    errorBox: {
      background: "#fff0f0", color: "#cc0000",
      padding: "10px 14px", borderRadius: 10,
      fontSize: 13, marginBottom: 12,
    },
    detailCard: {
      background: "#fff", margin: "16px",
      borderRadius: 14, padding: "16px 18px",
    },
    detailRow: {
      display: "flex", justifyContent: "space-between",
      alignItems: "center", padding: "10px 0",
      borderBottom: "1px solid #f5f5f3",
      fontSize: 14,
    },
    detailLabel: { color: "#888", fontSize: 13 },
    detailValue: { fontWeight: 600, color: "#111" },
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>
            qk<span style={{ color: "#FF6B35" }}>a</span>rd
          </div>
          <div style={{ color: "#aaa", fontSize: 14 }}>Loading your dashboard...</div>
        </div>
      </div>
    );
  }

  const hasLookbook = catalogue?.features?.hasLookbook || business?.features?.hasLookbook;
  const liveUrl = catalogue ? `https://qkard.app/${hasLookbook ? "look" : "c"}/${catalogue.slug}` : "";
  const amplifyUrl = catalogue ? `https://main.d3oyqx0xe6c90z.amplifyapp.com/c/${catalogue.slug}` : "";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.logo}>qk<span style={s.logoAccent}>a</span>rd</div>
        <button style={s.signOutBtn} onClick={handleSignOut}>Sign out</button>
      </div>

      <div style={s.body}>
        {/* Catalogue card */}
        {catalogue && (
          <div style={s.catalogueCard}>
            <div style={s.liveLabel}>● LIVE</div>
            <div style={s.shopName}>{business?.businessName}</div>
            <div style={s.shopMeta}>{business?.category} · {business?.city}</div>
            <div style={s.urlRow}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {liveUrl}
              </span>
              <button style={s.copyBtn} onClick={() => navigator.clipboard.writeText(liveUrl)}>
                Copy
              </button>
            </div>
            <div style={s.actionRow}>
              <button
                style={{ ...s.actionBtn, background: "#25D366", color: "#fff" }}
                onClick={() => window.open(`https://wa.me/?text=Check%20out%20my%20catalogue%3A%20${encodeURIComponent(liveUrl)}`, "_blank")}
              >
                Share on WhatsApp
              </button>
              <button
                style={{ ...s.actionBtn, background: "rgba(255,255,255,0.15)", color: "#fff" }}
                onClick={() => window.open(liveUrl, "_blank")}
              >
                View Live
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ ...s.tabs, overflowX: "auto", scrollbarWidth: "none" }}>
          {[
            ...((!(catalogue?.features?.hasLookbook || business?.features?.hasLookbook)) ? [{ id: "catalogue", label: "Items" }] : []),
            ...((features?.hasOrders || catalogue?.features?.hasOrders) && !(catalogue?.features?.hasLookbook || business?.features?.hasLookbook) ? [{
              id: "orders",
              label: orders.filter(o => o.status === "pending").length > 0 && activeTab !== "orders"
                ? "Orders 🔴"
                : "Orders"
            }] : []),
            { id: "qr", label: "QR Code" },
            { id: "shop", label: "Shop" },
            ...((features?.hasOrders || catalogue?.features?.hasOrders) ? [{ id: "reports", label: "📊 Reports" }] : []),
            ...((features?.hasBooking || catalogue?.features?.hasBooking) ? [{ id: "appointments", label: "📅 Appts" }] : []),
            ...((features?.hasQueue || catalogue?.features?.hasQueue) ? [{ id: "queue", label: "🎫 Queue" }] : []),
            ...((catalogue?.features?.hasLookbook || business?.features?.hasLookbook) ? [{ id: "designs", label: "🖼 Designs" }] : []),
            ...((catalogue?.features?.hasLookbook || business?.features?.hasLookbook) ? [{ id: "wishlists", label: newWishlistCount > 0 && activeTab !== "wishlists" ? `❤ Wishlists 🔴` : "❤ Wishlists" }] : []),
            ...((catalogue?.features?.hasLookbook || business?.features?.hasLookbook) ? [{ id: "customers", label: "👤 Customers" }] : []),
            ...((catalogue?.features?.hasLookbook || business?.features?.hasLookbook) ? [{ id: "workorders", label: "📋 Orders" }] : []),
            { id: "settings", label: "Settings" },
            ...(catalogue?.slug === "milkas-dairy-lucknow-j532" || business?.hasRewards ? [{ id: "rewards", label: "Rewards" }] : []),
          ].map(t => (
            <div
              key={t.id}
              style={{ ...s.tab, whiteSpace: "nowrap", ...(activeTab === t.id ? s.tabActive : {}) }}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </div>
          ))}
        </div>

        {/* ── Orders tab ── */}
        {activeTab === "orders" && (
          <>
            <div style={s.sectionHeader}>
              <div>
                <div style={s.sectionTitle}>Orders</div>
                <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
                  {ordersMode === "live"
                    ? `Auto-refreshes every 10s · ${orders.length} active`
                    : `Completed orders`}
                </div>
              </div>
              <button
                style={{ ...s.addBtn, background: "#f5f5f3", color: "#444", border: "1px solid #e5e5e5" }}
                onClick={() => loadOrders(business.businessId, ordersMode)}
              >
                ↻ Refresh
              </button>
            </div>

            <div style={{ display: "flex", margin: "0 16px 16px", background: "#f5f5f3", borderRadius: 10, padding: 4 }}>
              {["live", "history"].map(mode => (
                <button
                  key={mode}
                  onClick={() => setOrdersMode(mode)}
                  style={{
                    flex: 1, padding: "8px",
                    borderRadius: 8, border: "none",
                    fontSize: 13, fontWeight: 600,
                    cursor: "pointer",
                    background: ordersMode === mode ? "#111" : "transparent",
                    color: ordersMode === mode ? "#fff" : "#888",
                    transition: "all 0.15s",
                  }}
                >
                  {mode === "live" ? "🔴 Live" : "📋 History"}
                </button>
              ))}
            </div>

            {ordersMode === "history" && orders.length > 0 && (() => {
              const today = new Date().toDateString();
              const todayOrders = orders.filter(o => new Date(o.createdAt).toDateString() === today);
              const todayTotal = todayOrders.reduce((t, o) =>
                t + (o.items || []).reduce((s, i) => s + i.price * i.qty, 0), 0);
              return (
                <div style={{
                  margin: "0 16px 16px",
                  background: "#111", borderRadius: 14,
                  padding: "14px 16px", color: "#fff",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Today's Revenue</div>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>₹{todayTotal.toLocaleString("en-IN")}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Orders Today</div>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{todayOrders.length}</div>
                  </div>
                </div>
              );
            })()}

            {ordersLoading && orders.length === 0 ? (
              <div style={s.emptyState}>
                <div style={{ color: "#aaa", fontSize: 14 }}>Loading orders...</div>
              </div>
            ) : orders.length === 0 ? (
              <div style={s.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>{ordersMode === "live" ? "🍽" : "📋"}</div>
                <div style={{ fontWeight: 600, color: "#666", marginBottom: 6 }}>
                  {ordersMode === "live" ? "No active orders" : "No completed orders yet"}
                </div>
                <div style={{ fontSize: 13 }}>
                  {ordersMode === "live" ? "New orders will appear here automatically" : "Completed orders will show here"}
                </div>
              </div>
            ) : ordersMode === "live" ? (
              // ── Queue-Orders view for live tab ──
              <TokenQueueView
                orders={orders}
                onUpdateStatus={handleUpdateOrderStatus}
                onGenerateInvoice={handleGenerateInvoice}
                category={business?.category}
              />
            ) : (
              <>
                <OrdersList orders={orders} ordersMode={ordersMode} onUpdateStatus={handleUpdateOrderStatus} onGenerateInvoice={handleGenerateInvoice} category={business?.category} />
                {ordersHasMore && (
                  <div style={{ padding: "0 16px 16px" }}>
                    <button
                      style={{
                        width: "100%", padding: 12,
                        borderRadius: 12, border: "1px solid #e5e5e5",
                        background: "#fff", color: "#444",
                        fontSize: 14, fontWeight: 600, cursor: "pointer",
                      }}
                      onClick={() => loadOrders(business.businessId, "history", true)}
                      disabled={loadingMore}
                    >
                      {loadingMore ? "Loading…" : `↓ Load more orders`}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── Items tab ── */}
        {activeTab === "catalogue" && (
          <>
            <div style={s.sectionHeader}>
              <div>
                <div style={s.sectionTitle}>Your items</div>
                <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
                  {items.length} item{items.length !== 1 ? "s" : ""} in catalogue
                </div>
              </div>
              <button style={s.addBtn} onClick={() => { setItemForm(emptyItem); setEditingItem(null); setShowAddItem(true); }}>
                + Add item
              </button>
            </div>

            {items.length === 0 ? (
              <div style={s.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
                <div style={{ fontWeight: 600, color: "#666", marginBottom: 6 }}>No items yet</div>
                <div>Add your first item to make your catalogue useful</div>
                <button
                  style={{ ...s.addBtn, marginTop: 16, padding: "10px 24px" }}
                  onClick={() => { setItemForm(emptyItem); setEditingItem(null); setShowAddItem(true); }}
                >
                  + Add first item
                </button>
              </div>
            ) : (
              items.map(item => (
                <div key={item.itemId} style={s.itemCard}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 10,
                    background: "#f5f5f3", flexShrink: 0,
                    overflow: "hidden", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    fontSize: 24,
                  }}>
                    {item.photoUrl ? (
                      <img src={item.photoUrl} alt={item.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={e => { e.target.style.display = "none"; e.target.parentElement.innerText = "📦"; }}
                      />
                    ) : "📦"}
                  </div>
                  <div style={s.itemInfo}>
                    <div style={s.itemName}>{item.name}</div>
                    <div style={s.itemMeta}>
                      {item.category}
                      {item.inStock === false && (
                        <span style={{ color: "#cc0000", marginLeft: 8 }}>· Out of stock</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={s.itemPrice}>
                      {item.price ? `₹${item.price}` : "—"}
                      {item.unit && <span style={{ fontSize: 11, color: "#888", fontWeight: 400 }}> / {item.unit.replace("per ", "")}</span>}
                    </div>
                    <div style={s.itemActions}>
                      <button style={s.editBtn} onClick={() => openEdit(item)}>Edit</button>
                      <button style={s.deleteBtn} onClick={() => handleDeleteItem(item.itemId)}>Del</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* ── QR Code tab ── */}
        {activeTab === "qr" && catalogue && (
          <CataloguePoster
            businessName={business?.businessName}
            category={business?.category}
            city={business?.city}
            liveUrl={liveUrl}
            slug={catalogue?.slug}
          />
        )}

        {/* ── Appointments tab ── */}
        {activeTab === "appointments" && (
          <AppointmentsTab
            businessId={business?.businessId}
            accentColor="#6c63ff"
          />
        )}

        {/* ── Reports tab ── */}
        {activeTab === "reports" && (
          <ReportsTab
            orders={reportOrders}
            loading={reportLoading}
            period={reportPeriod}
            onPeriodChange={(p) => { setReportPeriod(p); loadReportOrders(business.businessId, p); }}
            onRefresh={() => loadReportOrders(business.businessId, reportPeriod)}
          />
        )}

        {/* ── Queue tab ── */}
        {activeTab === "queue" && (
          <QueueTab
            businessId={business?.businessId}
            businessName={business?.businessName}
          />
        )}

        {/* ── Designs tab ── */}
        {activeTab === "designs" && (
          <DesignsTab
            business={business}
            catalogue={catalogue}
            items={items}
            onRefresh={() => {
              const catalogueId = catalogue?.catalogueId;
              if (catalogueId) {
                fetch(`${API}/items?catalogueId=${catalogueId}`)
                  .then(r => r.json())
                  .then(d => setItems(d.items || []));
              }
            }}
          />
        )}

        {/* ── Wishlists tab ── */}
        {activeTab === "wishlists" && (
          <WishlistsTab business={business} catalogue={catalogue} onNewCount={setNewWishlistCount} />
        )}

        {/* ── Customers tab ── */}
        {activeTab === "customers" && (
          <CustomersTab
            business={business}
            catalogueDefaults={catalogue?.measurementDefaults}
            vertical={business?.category}
          />
        )}

        {/* ── Work Orders tab ── */}
        {activeTab === "workorders" && (
          business?.category === "tailor" || business?.category === "boutique"
            ? <TailorWorkOrdersTab business={business} catalogue={catalogue} items={items} />
            : business?.category === "jewellery" || business?.category === "jewelry"
            ? <JewelleryWorkOrdersTab business={business} catalogue={catalogue} items={items} />
            : <WorkOrdersTab business={business} items={items} />
        )}

        {/* ── Settings tab ── */}
        {activeTab === "rewards" && (
          <RewardsTab
            businessId={business?.businessId}
            businessName={business?.name}
            apiBase="https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod"
          />
        )}

        {activeTab === "settings" && features && (
          <>
            {/* ── THEME PICKER ── */}
            <div style={{ margin: "0 16px 20px" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 4 }}>🎨 Shop Theme</div>
              <div style={{ fontSize: 12, color: "#aaa", marginBottom: 16 }}>
                Choose a colour theme for your catalogue page. Customers will see this when they scan your QR.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
                {THEMES.map(theme => (
                  <div
                    key={theme.id}
                    onClick={() => handleSaveTheme(theme.id)}
                    style={{
                      borderRadius: 14,
                      border: selectedTheme === theme.id ? `2.5px solid #111` : "2px solid #e5e5e5",
                      padding: "12px 10px",
                      cursor: "pointer",
                      background: theme.bg,
                      textAlign: "center",
                      position: "relative",
                      transition: "transform 0.15s, box-shadow 0.15s",
                      boxShadow: selectedTheme === theme.id ? "0 4px 16px rgba(0,0,0,0.15)" : "none",
                      transform: selectedTheme === theme.id ? "scale(1.03)" : "scale(1)",
                    }}
                  >
                    {selectedTheme === theme.id && (
                      <div style={{
                        position: "absolute", top: -8, right: -8,
                        width: 20, height: 20, borderRadius: "50%",
                        background: "#111", color: "white",
                        fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 700,
                      }}>✓</div>
                    )}
                    {/* Color swatches */}
                    <div style={{ display: "flex", gap: 4, justifyContent: "center", marginBottom: 8 }}>
                      {theme.preview.map((c, i) => (
                        <div key={i} style={{
                          width: i === 0 ? 24 : 16, height: 16,
                          borderRadius: 4, background: c,
                          border: "1px solid rgba(0,0,0,0.1)",
                        }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{theme.emoji}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: theme.text }}>{theme.name}</div>
                  </div>
                ))}
              </div>
              {themeSaved && (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600, color: "#166534", textAlign: "center" }}>
                  ✅ Theme saved! Customers will see the new theme instantly.
                </div>
              )}
              {savingTheme && (
                <div style={{ textAlign: "center", fontSize: 13, color: "#aaa" }}>Saving theme…</div>
              )}
            </div>

            <div style={{ height: 1, background: "#f0f0f0", margin: "0 16px 20px" }} />

            <div style={s.sectionHeader}>
              <div>
                <div style={s.sectionTitle}>Features</div>
                <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
                  Enable or disable features for your shop
                </div>
              </div>
            </div>

            <div style={{ margin: "0 16px" }}>
              {[
                ...((!(catalogue?.features?.hasLookbook || business?.features?.hasLookbook)) ? [
                  { key: "hasOrders", label: "Orders & Cart", desc: "Customers can add to cart and place orders", icon: "🛒" },
                ] : []),
                ...(["Restaurant", "Bakery", "Sweet Shop", "Dhaba", "Hotel", "Resort", "Banquet"].includes(business?.category) ? [
                  { key: "hasTables", label: "Table QR Codes", desc: "Generate QR codes for each table", icon: "🪑" },
                  { key: "hasWaiter", label: "Waiter Dashboard", desc: "Assign orders to waiters by table", icon: "🧑‍🍳" },
                ] : []),
                { key: "hasBooking", label: "Appointments & Booking", desc: "Customers can book slots or appointments", icon: "📅" },
                ...((!(catalogue?.features?.hasLookbook || business?.features?.hasLookbook)) ? [
                  { key: "hasDelivery", label: "Delivery Address", desc: "Collect delivery address at checkout", icon: "🚚" },
                ] : []),
                { key: "hasQueue", label: "Queue System", desc: "Manage customer queue and wait time", icon: "📋" },
              ].map(({ key, label, desc, icon }) => (
                <div key={key} style={{
                  background: "#fff", borderRadius: 14,
                  padding: "14px 16px", marginBottom: 10,
                  display: "flex", alignItems: "center", gap: 14,
                  border: "1px solid #efefed",
                }}>
                  <span style={{ fontSize: 24 }}>{icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#111", marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>{desc}</div>
                  </div>
                  <button
                    onClick={() => handleSaveFeatures({ ...features, [key]: !features[key] })}
                    style={{
                      width: 44, height: 24, borderRadius: 12,
                      border: "none", cursor: "pointer",
                      position: "relative",
                      background: features[key] ? "#22c55e" : "#e5e5e5",
                      transition: "background 0.2s",
                      flexShrink: 0,
                    }}
                  >
                    <div style={{
                      position: "absolute",
                      top: 3, left: features[key] ? 23 : 3,
                      width: 18, height: 18,
                      borderRadius: 9, background: "#fff",
                      transition: "left 0.2s",
                    }} />
                  </button>
                </div>
              ))}
            </div>

            <div style={{ margin: "16px", padding: "12px 16px", background: "#fffbeb", borderRadius: 12, border: "1px solid #fcd34d" }}>
              <div style={{ fontSize: 12, color: "#92400e", fontWeight: 600, marginBottom: 4 }}>💡 Auto-configured</div>
              <div style={{ fontSize: 12, color: "#92400e" }}>
                Features were automatically set based on your category ({business?.category}). You can change them anytime.
              </div>
            </div>

            {/* GST Profile */}
            <div style={{ margin: "0 16px 16px" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 4 }}>🧾 GST & Invoice Profile</div>
              <div style={{ fontSize: 12, color: "#aaa", marginBottom: 14 }}>
                Used on invoices. Leave GSTIN blank if unregistered — system will generate a simple bill.
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
                    GSTIN (optional)
                  </label>
                  <input
                    style={{ ...s.input, marginBottom: 0 }}
                    placeholder="e.g. 09AAAAA0000A1Z5"
                    value={gstProfile.gstin}
                    onChange={e => setGstProfile(p => ({ ...p, gstin: e.target.value.toUpperCase() }))}
                    maxLength={15}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
                    Legal Business Name
                  </label>
                  <input
                    style={{ ...s.input, marginBottom: 0 }}
                    placeholder={business?.businessName}
                    value={gstProfile.legalName}
                    onChange={e => setGstProfile(p => ({ ...p, legalName: e.target.value }))}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
                    Business Address
                  </label>
                  <input
                    style={{ ...s.input, marginBottom: 0 }}
                    placeholder="Shop address for invoice"
                    value={gstProfile.address}
                    onChange={e => setGstProfile(p => ({ ...p, address: e.target.value }))}
                  />
                </div>
                <button
                  style={{
                    ...s.addBtn,
                    width: "100%", padding: 12, borderRadius: 10,
                    background: gstSaved ? "#22c55e" : "#111",
                  }}
                  onClick={handleSaveGSTProfile}
                  disabled={savingGST}
                >
                  {savingGST ? "Saving…" : gstSaved ? "✓ Saved!" : "Save GST Profile"}
                </button>
              </div>
            </div>

            {/* Booking Settings — only for hasBooking shops */}
            {(features?.hasBooking || catalogue?.features?.hasBooking) && (
              <div style={{ margin: "0 16px 16px" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 4 }}>📅 Appointment Settings</div>
                <div style={{ fontSize: 12, color: "#aaa", marginBottom: 14 }}>
                  Set your working hours and slot duration for bookings
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
                        Opening Time
                      </label>
                      <input
                        type="time"
                        style={{ ...s.input, marginBottom: 0 }}
                        value={bookingSettings.openTime}
                        onChange={e => setBookingSettings(p => ({ ...p, openTime: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
                        Closing Time
                      </label>
                      <input
                        type="time"
                        style={{ ...s.input, marginBottom: 0 }}
                        value={bookingSettings.closeTime}
                        onChange={e => setBookingSettings(p => ({ ...p, closeTime: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
                        Slot Duration
                      </label>
                      <select
                        style={{ ...s.select, marginBottom: 0 }}
                        value={bookingSettings.slotMinutes}
                        onChange={e => setBookingSettings(p => ({ ...p, slotMinutes: Number(e.target.value) }))}
                      >
                        <option value={15}>15 minutes</option>
                        <option value={20}>20 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={90}>1.5 hours</option>
                        <option value={120}>2 hours</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
                        Max per Slot
                      </label>
                      <select
                        style={{ ...s.select, marginBottom: 0 }}
                        value={bookingSettings.maxPerSlot}
                        onChange={e => setBookingSettings(p => ({ ...p, maxPerSlot: Number(e.target.value) }))}
                      >
                        <option value={1}>1 person</option>
                        <option value={2}>2 people</option>
                        <option value={3}>3 people</option>
                        <option value={5}>5 people</option>
                      </select>
                    </div>
                  </div>

                  {/* Preview */}
                  <div style={{ background: "#f9f9ff", border: "1px solid #e5e5ff", borderRadius: 10, padding: "10px 14px" }}>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Preview</div>
                    <div style={{ fontSize: 13, color: "#444" }}>
                      Open {bookingSettings.openTime} – {bookingSettings.closeTime} ·{" "}
                      {bookingSettings.slotMinutes} min slots ·{" "}
                      {Math.floor((
                        (parseInt(bookingSettings.closeTime) * 60 + parseInt(bookingSettings.closeTime.split(":")[1])) -
                        (parseInt(bookingSettings.openTime) * 60 + parseInt(bookingSettings.openTime.split(":")[1]))
                      ) / bookingSettings.slotMinutes)} slots/day
                    </div>
                  </div>

                  <button
                    style={{
                      ...s.addBtn,
                      width: "100%", padding: 12, borderRadius: 10,
                      background: bookingSaved ? "#22c55e" : "#6c63ff",
                    }}
                    onClick={handleSaveBookingSettings}
                    disabled={savingBooking}
                  >
                    {savingBooking ? "Saving…" : bookingSaved ? "✓ Saved!" : "Save Appointment Settings"}
                  </button>
                </div>
              </div>
            )}

          </>
        )}

        {/* ── Shop details tab ── */}
        {activeTab === "shop" && business && (
          <>
            <div style={{ margin: "16px", background: "#fff", borderRadius: 16, overflow: "hidden", border: "1px solid #efefed" }}>
              <div style={{
                height: 140, background: catalogue?.coverPhoto ? `url(${catalogue.coverPhoto}) center/cover` : "linear-gradient(135deg, #111 0%, #333 100%)",
                position: "relative",
              }}>
                <button
                  onClick={() => coverRef.current?.click()}
                  style={{
                    position: "absolute", bottom: 10, right: 10,
                    background: "rgba(0,0,0,0.6)", color: "#fff",
                    border: "1px solid rgba(255,255,255,0.3)",
                    borderRadius: 8, padding: "6px 12px",
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  {uploadingCover ? "Uploading..." : "📷 Change Cover"}
                </button>
                <input ref={coverRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => handleMediaUpload(e.target.files?.[0], "coverPhoto")} />
              </div>

              <div style={{ padding: "0 16px 16px", display: "flex", alignItems: "flex-start", gap: 14, marginTop: -30 }}>
                <div style={{ position: "relative" }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 14,
                    border: "3px solid #fff", background: catalogue?.logo ? "transparent" : "#111",
                    overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                  }}>
                    {catalogue?.logo
                      ? <img src={catalogue.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ color: "#f5c542", fontSize: 22, fontWeight: 700 }}>{business.businessName?.[0]}</span>
                    }
                  </div>
                  <button
                    onClick={() => logoRef.current?.click()}
                    style={{
                      position: "absolute", bottom: -4, right: -4,
                      width: 22, height: 22, borderRadius: "50%",
                      background: "#111", color: "#fff", border: "2px solid #fff",
                      fontSize: 11, cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {uploadingLogo ? "..." : "✎"}
                  </button>
                  <input ref={logoRef} type="file" accept="image/*" style={{ display: "none" }}
                    onChange={e => handleMediaUpload(e.target.files?.[0], "logo")} />
                </div>
                <div style={{ paddingTop: 34 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#111" }}>{business.businessName}</div>
                  <div style={{ fontSize: 13, color: "#888" }}>{business.category} · {business.city}</div>
                </div>
              </div>
            </div>

            <div style={s.detailCard}>
              {[
                ["Shop name", business.businessName],
                ["Category", business.category],
                ["City", business.city],
                ["Phone", business.phone],
                ["Plan", business.plan?.toUpperCase() || "FREE"],
                ["Status", business.status?.toUpperCase() || "ACTIVE"],
              ].map(([label, value]) => (
                <div key={label} style={s.detailRow}>
                  <span style={s.detailLabel}>{label}</span>
                  <span style={s.detailValue}>{value}</span>
                </div>
              ))}
              <div style={{ marginTop: 16, fontSize: 13, color: "#aaa", textAlign: "center" }}>
                To update shop details, contact support
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add / Edit item modal */}
      {showAddItem && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) { setShowAddItem(false); setError(""); } }}>
          <div style={s.modal}>
            <div style={s.modalTitle}>
              {editingItem ? "Edit item" : "Add new item"}
            </div>

            {error && <div style={s.errorBox}>{error}</div>}

            <label style={s.label}>Item name *</label>
            <input
              style={s.input}
              placeholder="e.g. Kaju Katli"
              value={itemForm.name}
              onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))}
            />

            <label style={s.label}>Photo</label>
            <PhotoPicker
              itemName={itemForm.name}
              itemId={editingItem?.itemId || "new-" + Date.now()}
              businessId={business?.businessId}
              currentPhoto={itemForm.photoUrl}
              onPhotoSelected={(photo) => setItemForm(f => ({ ...f, photoUrl: photo?.url || null }))}
            />

            <label style={s.label}>Price (₹)</label>
            <input
              style={s.input}
              type="number"
              placeholder="e.g. 800"
              value={itemForm.price}
              onChange={e => setItemForm(f => ({ ...f, price: e.target.value }))}
            />

            <label style={s.label}>Unit</label>
            <select
              style={s.select}
              value={itemForm.unit}
              onChange={e => setItemForm(f => ({ ...f, unit: e.target.value }))}
            >
              <option value="">No unit</option>
              <option value="per kg">per kg</option>
              <option value="per 500g">per 500g</option>
              <option value="per 250g">per 250g</option>
              <option value="per 100g">per 100g</option>
              <option value="per gram">per gram</option>
              <option value="per litre">per litre</option>
              <option value="per 500ml">per 500ml</option>
              <option value="per piece">per piece</option>
              <option value="per dozen">per dozen</option>
              <option value="per packet">per packet</option>
              <option value="per box">per box</option>
              <option value="per bottle">per bottle</option>
              <option value="per metre">per metre</option>
              <option value="per yard">per yard</option>
              <option value="per set">per set</option>
            </select>

            <label style={s.label}>Category</label>
            <select
              style={s.select}
              value={itemForm.category}
              onChange={e => setItemForm(f => ({ ...f, category: e.target.value }))}
            >
              {ITEM_CATEGORIES.filter(c => c !== "All").map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <label style={s.label}>Description</label>
            <input
              style={s.input}
              placeholder="Optional short description"
              value={itemForm.description}
              onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))}
            />

            <div style={s.stockRow}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>In stock</span>
              <button
                style={{ ...s.toggle, background: itemForm.inStock ? "#22c55e" : "#e5e5e5" }}
                onClick={() => setItemForm(f => ({ ...f, inStock: !f.inStock }))}
              >
                <div style={{
                  position: "absolute",
                  top: 3, left: itemForm.inStock ? 23 : 3,
                  width: 18, height: 18,
                  borderRadius: 9, background: "#fff",
                  transition: "left 0.2s",
                }} />
              </button>
            </div>

            <button
              style={{ ...s.saveBtn, ...(saving ? { background: "#ccc" } : {}) }}
              onClick={handleSaveItem}
              disabled={saving}
            >
              {saving ? "Saving..." : editingItem ? "Save changes" : "Add item"}
            </button>
            <button style={s.cancelBtn} onClick={() => { setShowAddItem(false); setError(""); setEditingItem(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Catalogue QR Poster ───────────────────────────────────────────────────────
function CataloguePoster({ businessName, category, city, liveUrl, slug }) {
  const canvasRef = React.useRef(null);
  const [qrDataUrl, setQrDataUrl] = React.useState(null);
  const getDefaultTheme = (cat = "") => { const c = cat.toLowerCase(); if (["salon", "beauty", "spa"].some(k => c.includes(k))) return "rose"; if (["grocery", "pharmacy", "medical", "kirana"].some(k => c.includes(k))) return "forest"; if (["electronics", "mobile", "tech"].some(k => c.includes(k))) return "midnight"; if (["clothing", "fashion", "tailor", "boutique"].some(k => c.includes(k))) return "ocean"; return "vibrant"; };
  const [theme, setTheme] = React.useState(getDefaultTheme(category));

  const themes = {
    vibrant: {
      bg: ["#FF6B35", "#f5c542"],
      qrDark: "#1a0a00",
      qrLight: "#fff8f0",
      titleColor: "#fff",
      subtitleColor: "rgba(255,255,255,0.85)",
      tagBg: "rgba(255,255,255,0.2)",
      tagColor: "#fff",
      urlColor: "rgba(255,255,255,0.7)",
      brandColor: "#fff",
      label: "🔥 Vibrant Orange",
    },
    midnight: {
      bg: ["#0f0c29", "#302b63"],
      qrDark: "#6c63ff",
      qrLight: "#ffffff",
      titleColor: "#fff",
      subtitleColor: "rgba(255,255,255,0.7)",
      tagBg: "rgba(108,99,255,0.3)",
      tagColor: "#a5b4fc",
      urlColor: "rgba(255,255,255,0.5)",
      brandColor: "#6c63ff",
      label: "🌙 Midnight Purple",
    },
    forest: {
      bg: ["#134e4a", "#065f46"],
      qrDark: "#064e3b",
      qrLight: "#f0fdf4",
      titleColor: "#fff",
      subtitleColor: "rgba(255,255,255,0.75)",
      tagBg: "rgba(34,197,94,0.25)",
      tagColor: "#86efac",
      urlColor: "rgba(255,255,255,0.55)",
      brandColor: "#4ade80",
      label: "🌿 Forest Green",
    },
    rose: {
      bg: ["#be185d", "#9d174d"],
      qrDark: "#831843",
      qrLight: "#fff1f2",
      titleColor: "#fff",
      subtitleColor: "rgba(255,255,255,0.8)",
      tagBg: "rgba(255,255,255,0.2)",
      tagColor: "#fce7f3",
      urlColor: "rgba(255,255,255,0.6)",
      brandColor: "#fbcfe8",
      label: "🌹 Rose Pink",
    },
    ocean: {
      bg: ["#0369a1", "#0284c7"],
      qrDark: "#0c4a6e",
      qrLight: "#f0f9ff",
      titleColor: "#fff",
      subtitleColor: "rgba(255,255,255,0.8)",
      tagBg: "rgba(255,255,255,0.2)",
      tagColor: "#bae6fd",
      urlColor: "rgba(255,255,255,0.6)",
      brandColor: "#7dd3fc",
      label: "🌊 Ocean Blue",
    },
  };

  const t = themes[theme];

  // Generate QR
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    import("qrcode").then(QRCode => {
      QRCode.default.toDataURL(liveUrl, {
        width: 400, margin: 1,
        color: { dark: t.qrDark, light: t.qrLight },
        errorCorrectionLevel: "H",
      }).then(setQrDataUrl).catch(() => { });
    });
  }, [liveUrl, theme, t.qrDark, t.qrLight]);

  const downloadPoster = () => {
    const canvas = canvasRef.current;
    if (!canvas || !qrDataUrl) return;
    const ctx = canvas.getContext("2d");
    const W = 800, H = 1100;
    canvas.width = W; canvas.height = H;

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, t.bg[0]);
    grad.addColorStop(1, t.bg[1]);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Decorative circles
    ctx.beginPath();
    ctx.arc(W + 100, -100, 350, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    ctx.fill();

    ctx.beginPath();
    ctx.arc(-100, H + 50, 300, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    ctx.fill();

    ctx.textAlign = "center";

    // Category tag
    const tag = category || "Shop";
    ctx.font = "bold 15px sans-serif";
    const tagW = ctx.measureText(tag.toUpperCase()).width + 32;
    roundRectFill(ctx, W / 2 - tagW / 2, 52, tagW, 34, 17, "rgba(255,255,255,0.18)");
    ctx.fillStyle = t.tagColor;
    ctx.font = "bold 13px sans-serif";
    ctx.letterSpacing = "2px";
    ctx.fillText(tag.toUpperCase(), W / 2, 75);

    // Business name — big and bold
    ctx.fillStyle = t.titleColor;
    ctx.font = "bold 64px sans-serif";
    ctx.letterSpacing = "-1px";
    const name = businessName || "My Shop";
    if (ctx.measureText(name).width > 720) {
      ctx.font = "bold 48px sans-serif";
    }
    ctx.fillText(name, W / 2, 170);

    // City
    ctx.fillStyle = t.subtitleColor;
    ctx.font = "500 22px sans-serif";
    ctx.letterSpacing = "0px";
    ctx.fillText(city || "India", W / 2, 210);

    // Divider line
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(120, 240); ctx.lineTo(W - 120, 240);
    ctx.stroke();

    // "SCAN TO ORDER" text
    ctx.fillStyle = t.titleColor;
    ctx.font = "800 20px sans-serif";
    ctx.letterSpacing = "6px";
    ctx.fillText("SCAN TO ORDER", W / 2, 284);

    // QR white card
    const qrSize = 380;
    const qrX = W / 2 - qrSize / 2;
    const qrY = 308;
    roundRectFill(ctx, qrX - 16, qrY - 16, qrSize + 32, qrSize + 32, 28, "#ffffff");

    // QR image
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

      // "NO PAYMENT" badge
      roundRectFill(ctx, W / 2 - 140, 740, 280, 44, 22, "rgba(255,255,255,0.15)");
      ctx.fillStyle = t.tagColor;
      ctx.font = "bold 14px sans-serif";
      ctx.letterSpacing = "0px";
      ctx.fillText("✓ NO PAYMENT   ✓ NO APP NEEDED", W / 2, 768);

      // Instructions
      ctx.fillStyle = t.subtitleColor;
      ctx.font = "18px sans-serif";
      ctx.fillText("Open camera → point at QR → tap the link", W / 2, 820);

      // URL
      ctx.fillStyle = t.urlColor;
      ctx.font = "14px monospace";
      ctx.fillText(liveUrl, W / 2, 858);

      // Bottom divider
      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.beginPath();
      ctx.moveTo(120, 882); ctx.lineTo(W - 120, 882);
      ctx.stroke();

      // QKARD brand
      ctx.fillStyle = t.brandColor;
      ctx.font = "bold 28px sans-serif";
      ctx.letterSpacing = "4px";
      ctx.fillText("QKARD", W / 2, 930);

      ctx.fillStyle = t.subtitleColor;
      ctx.font = "14px sans-serif";
      ctx.letterSpacing = "0px";
      ctx.fillText("Digital Catalogue & Ordering", W / 2, 958);

      // Bottom dots decoration
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.arc(W / 2 - 40 + i * 20, 1000, i === 2 ? 5 : 3, 0, Math.PI * 2);
        ctx.fillStyle = i === 2 ? t.brandColor : "rgba(255,255,255,0.3)";
        ctx.fill();
      }

      const a = document.createElement("a");
      a.download = `qkard-poster-${(businessName || "shop").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = qrDataUrl;
  };

  function roundRectFill(ctx, x, y, w, h, r, color) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  }

  return (
    <div style={{ padding: "16px 16px 40px", maxWidth: 480, margin: "0 auto" }}>

      {/* Theme selector */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: "#888", fontWeight: 600, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
          Choose Poster Style
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {Object.entries(themes).map(([key, th]) => (
            <button key={key} onClick={() => setTheme(key)} style={{
              padding: "8px 14px", borderRadius: 20,
              border: `2px solid ${theme === key ? th.bg[0] : "#e5e5e5"}`,
              background: theme === key
                ? `linear-gradient(135deg, ${th.bg[0]}, ${th.bg[1]})`
                : "#fff",
              color: theme === key ? "#fff" : "#555",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              transition: "all 0.15s",
            }}>
              {th.label}
            </button>
          ))}
        </div>
      </div>

      {/* Poster preview */}
      <div style={{
        background: `linear-gradient(135deg, ${t.bg[0]}, ${t.bg[1]})`,
        borderRadius: 20, overflow: "hidden",
        padding: "28px 20px 24px",
        marginBottom: 16,
        position: "relative",
      }}>
        {/* Decorative circles */}
        <div style={{
          position: "absolute", top: -60, right: -60,
          width: 200, height: 200, borderRadius: "50%",
          background: "rgba(255,255,255,0.07)",
        }} />
        <div style={{
          position: "absolute", bottom: -40, left: -40,
          width: 160, height: 160, borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
        }} />

        {/* Category tag */}
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <span style={{
            background: t.tagBg, color: t.tagColor,
            borderRadius: 20, padding: "5px 16px",
            fontSize: 11, fontWeight: 700, letterSpacing: 2,
          }}>
            {(category || "SHOP").toUpperCase()}
          </span>
        </div>

        {/* Business name */}
        <div style={{
          textAlign: "center",
          fontSize: businessName?.length > 16 ? 22 : 28,
          fontWeight: 900,
          color: t.titleColor,
          letterSpacing: "-0.5px",
          marginBottom: 4,
          lineHeight: 1.1,
        }}>
          {businessName || "Your Shop"}
        </div>

        {/* City */}
        <div style={{ textAlign: "center", fontSize: 14, color: t.subtitleColor, marginBottom: 16 }}>
          {city || "India"}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.2)", marginBottom: 14 }} />

        {/* SCAN TO ORDER */}
        <div style={{
          textAlign: "center", fontSize: 11,
          fontWeight: 800, letterSpacing: 4,
          color: t.titleColor, marginBottom: 12,
        }}>
          SCAN TO ORDER
        </div>

        {/* QR Code */}
        <div style={{
          background: "#fff", borderRadius: 20,
          padding: 14, margin: "0 auto",
          width: "fit-content",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}>
          {qrDataUrl
            ? <img src={qrDataUrl} alt="QR" style={{ width: 180, height: 180, display: "block", borderRadius: 8 }} />
            : <div style={{ width: 180, height: 180, background: "#f5f5f5", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#aaa", fontSize: 13 }}>Generating…</div>
          }
        </div>

        {/* No payment badge */}
        <div style={{
          textAlign: "center", marginTop: 14,
          background: "rgba(255,255,255,0.15)",
          borderRadius: 12, padding: "8px 16px",
          fontSize: 12, fontWeight: 700, color: t.tagColor,
        }}>
          ✓ No Payment &nbsp;&nbsp; ✓ No App Needed
        </div>

        {/* Instructions */}
        <div style={{ textAlign: "center", marginTop: 8, fontSize: 12, color: t.subtitleColor }}>
          Open camera → point at QR → tap the link
        </div>

        {/* URL */}
        <div style={{ textAlign: "center", marginTop: 4, fontSize: 10, color: t.urlColor, fontFamily: "monospace", wordBreak: "break-all" }}>
          {liveUrl}
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.15)", margin: "14px 0 12px" }} />

        {/* Brand */}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: 4, color: t.brandColor }}>QKARD</div>
          <div style={{ fontSize: 11, color: t.subtitleColor, marginTop: 2 }}>Digital Catalogue & Ordering</div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button
          onClick={downloadPoster}
          disabled={!qrDataUrl}
          style={{
            padding: 14, borderRadius: 12, border: "none",
            background: `linear-gradient(135deg, ${t.bg[0]}, ${t.bg[1]})`,
            color: "#fff", fontSize: 15, fontWeight: 700,
            cursor: qrDataUrl ? "pointer" : "not-allowed",
            opacity: qrDataUrl ? 1 : 0.6,
          }}
        >
          ⬇ Download Poster (Print Ready)
        </button>
        <button
          style={{ padding: 14, borderRadius: 12, border: "none", background: "#25D366", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}
          onClick={() => window.open(`https://wa.me/?text=Order from ${businessName}: ${liveUrl}`, "_blank")}
        >
          📲 Share on WhatsApp
        </button>
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}

function ReportsTab({ orders, loading, period, onPeriodChange, onRefresh }) {
  const now = new Date();

  // ── Date helpers ──────────────────────────────────────────────────────────
  function startOf(p) {
    const d = new Date();
    if (p === "today") { d.setHours(0, 0, 0, 0); return d; }
    if (p === "week") { d.setDate(d.getDate() - 6); d.setHours(0, 0, 0, 0); return d; }
    if (p === "month") { d.setDate(d.getDate() - 29); d.setHours(0, 0, 0, 0); return d; }
    return d;
  }

  function orderTotal(o) {
    return (o.items || []).reduce((s, i) => s + i.price * i.qty, 0);
  }

  // ── Filter by period ──────────────────────────────────────────────────────
  const start = startOf(period);
  const filtered = orders.filter(o => new Date(o.createdAt) >= start);
  const revenue = filtered.reduce((s, o) => s + orderTotal(o), 0);
  const count = filtered.length;
  const avg = count > 0 ? Math.round(revenue / count) : 0;

  // ── Today stats always shown as sub-info ──────────────────────────────────
  const todayStart = startOf("today");
  const todayOrders = orders.filter(o => new Date(o.createdAt) >= todayStart);
  const todayRevenue = todayOrders.reduce((s, o) => s + orderTotal(o), 0);

  // ── Daily breakdown for bar chart ─────────────────────────────────────────
  const days = period === "today" ? 1 : period === "week" ? 7 : 30;
  const dailyData = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    const dayOrders = orders.filter(o => {
      const od = new Date(o.createdAt);
      return od.getDate() === d.getDate() &&
        od.getMonth() === d.getMonth() &&
        od.getFullYear() === d.getFullYear();
    });
    const dayRevenue = dayOrders.reduce((s, o) => s + orderTotal(o), 0);
    dailyData.push({ key, revenue: dayRevenue, count: dayOrders.length });
  }

  const maxRevenue = Math.max(...dailyData.map(d => d.revenue), 1);

  // ── Top items ─────────────────────────────────────────────────────────────
  const itemMap = {};
  filtered.forEach(o => {
    (o.items || []).forEach(i => {
      if (!itemMap[i.name]) itemMap[i.name] = { qty: 0, revenue: 0 };
      itemMap[i.name].qty += i.qty;
      itemMap[i.name].revenue += i.price * i.qty;
    });
  });
  const topItems = Object.entries(itemMap)
    .sort((a, b) => b[1].revenue - a[1].revenue)
    .slice(0, 5);

  const periodLabel = { today: "Today", week: "Last 7 Days", month: "Last 30 Days" }[period];

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#aaa", fontSize: 14 }}>
      Loading reports…
    </div>
  );

  if (orders.length === 0) return (
    <div style={{ padding: 48, textAlign: "center" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
      <div style={{ fontWeight: 600, color: "#666", marginBottom: 6 }}>No sales data yet</div>
      <div style={{ fontSize: 13, color: "#aaa" }}>Reports will appear once orders are billed</div>
    </div>
  );

  return (
    <div style={{ padding: "16px 16px 40px" }}>

      {/* Period selector */}
      <div style={{ display: "flex", background: "#f5f5f3", borderRadius: 10, padding: 4, marginBottom: 16 }}>
        {[
          { id: "today", label: "Today" },
          { id: "week", label: "7 Days" },
          { id: "month", label: "30 Days" },
        ].map(p => (
          <button key={p.id} onClick={() => onPeriodChange(p.id)} style={{
            flex: 1, padding: "8px", borderRadius: 8, border: "none",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            background: period === p.id ? "#111" : "transparent",
            color: period === p.id ? "#fff" : "#888",
            transition: "all 0.15s",
          }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Today's quick stat — always visible */}
      {period !== "today" && (
        <div style={{
          background: "#f0fdf4", border: "1px solid #86efac",
          borderRadius: 12, padding: "10px 14px",
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 14,
        }}>
          <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>🟢 Today so far</span>
          <span style={{ fontSize: 15, fontWeight: 800, color: "#15803d" }}>
            ₹{todayRevenue.toLocaleString("en-IN")} · {todayOrders.length} orders
          </span>
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Revenue", value: `₹${revenue.toLocaleString("en-IN")}`, color: "#111" },
          { label: "Orders", value: count, color: "#6366f1" },
          { label: "Avg Order", value: `₹${avg.toLocaleString("en-IN")}`, color: "#FF6B35" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: "#fff", border: "1px solid #efefed",
            borderRadius: 14, padding: "14px 10px", textAlign: "center",
          }}>
            <div style={{ fontSize: 11, color: "#aaa", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
              {label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Bar chart */}
      {days > 1 && (
        <div style={{ background: "#fff", border: "1px solid #efefed", borderRadius: 14, padding: "16px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 14 }}>
            Daily Revenue — {periodLabel}
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: days <= 7 ? 8 : 4, height: 120 }}>
            {dailyData.map((d, i) => {
              const pct = maxRevenue > 0 ? (d.revenue / maxRevenue) : 0;
              const barH = Math.max(pct * 100, d.revenue > 0 ? 4 : 0);
              const isToday = i === dailyData.length - 1;
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  {d.revenue > 0 && (
                    <div style={{ fontSize: 9, color: "#888", textAlign: "center", lineHeight: 1.2 }}>
                      ₹{d.revenue >= 1000 ? `${(d.revenue / 1000).toFixed(1)}k` : d.revenue}
                    </div>
                  )}
                  <div style={{
                    width: "100%", height: `${barH}px`,
                    background: isToday ? "#111" : "#e5e7eb",
                    borderRadius: "4px 4px 0 0",
                    minHeight: 2,
                    transition: "height 0.3s",
                  }} />
                  {days <= 7 && (
                    <div style={{ fontSize: 9, color: isToday ? "#111" : "#aaa", fontWeight: isToday ? 700 : 400, textAlign: "center" }}>
                      {d.key}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {days > 7 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 10, color: "#aaa" }}>{dailyData[0]?.key}</span>
              <span style={{ fontSize: 10, color: "#111", fontWeight: 700 }}>Today</span>
            </div>
          )}
        </div>
      )}

      {/* Top items */}
      {topItems.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #efefed", borderRadius: 14, padding: "16px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 14 }}>
            🏆 Top Items — {periodLabel}
          </div>
          {topItems.map(([name, stats], i) => {
            const pct = (stats.revenue / topItems[0][1].revenue) * 100;
            return (
              <div key={name} style={{ marginBottom: i < topItems.length - 1 ? 12 : 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`} {name}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>
                    ₹{stats.revenue.toLocaleString("en-IN")}
                    <span style={{ fontSize: 11, color: "#aaa", fontWeight: 400 }}> · {stats.qty} sold</span>
                  </span>
                </div>
                <div style={{ height: 4, background: "#f5f5f3", borderRadius: 2 }}>
                  <div style={{ height: 4, width: `${pct}%`, background: "#111", borderRadius: 2, transition: "width 0.4s" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Refresh */}
      <button onClick={onRefresh} style={{
        width: "100%", background: "#f5f5f3",
        border: "1px solid #e5e5e5", borderRadius: 12,
        padding: 12, fontSize: 13, color: "#666",
        fontWeight: 600, cursor: "pointer",
      }}>
        ↻ Refresh Reports
      </button>

    </div>
  );
}

// ── Token Queue View — falooda/sweet shop live orders as queue ───────────────
function TokenQueueView({ orders, onUpdateStatus, onGenerateInvoice, category }) {
  // Sort by token number numerically
  const sorted = [...orders].sort((a, b) => {
    const numA = parseInt(String(a.tokenNumber || 0).replace("TK-", "")) || 0;
    const numB = parseInt(String(b.tokenNumber || 0).replace("TK-", "")) || 0;
    return numA - numB;
  });

  const serving = sorted.find(o => o.status === "preparing");
  const pending = sorted.filter(o => o.status === "pending");
  const done = sorted.filter(o => o.status === "done");
  const next = pending[0] || null;

  const orderTotal = (o) => (o.items || []).reduce((t, i) => t + i.price * i.qty, 0);
  const tokenNum = (o) => o ? String(o.tokenNumber || "").replace("TK-", "") : "";

  return (
    <div style={{ padding: "0 16px 40px", maxWidth: 560, margin: "0 auto" }}>

      {/* Counters */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
        {[
          { label: "WAITING", count: pending.length, color: "#FF6B35" },
          { label: "PREPARING", count: serving ? 1 : 0, color: "#f5c542" },
          { label: "DONE", count: done.length, color: "#22c55e" },
        ].map(({ label, count, color }) => (
          <div key={label} style={{
            background: "#fff", border: "1px solid #efefed",
            borderRadius: 12, padding: "12px 8px", textAlign: "center",
          }}>
            <div style={{ fontSize: 28, fontWeight: 900, color, fontFamily: "monospace" }}>{count}</div>
            <div style={{ fontSize: 10, color: "#aaa", fontWeight: 700, letterSpacing: 1 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Now serving */}
      {serving && (
        <div style={{
          background: "#0d2a0d", border: "2px solid #22c55e",
          borderRadius: 16, padding: 20, marginBottom: 16,
        }}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: "#22c55e", fontWeight: 700, marginBottom: 8 }}>
            NOW PREPARING
          </div>
          <div style={{ fontSize: 52, fontWeight: 900, fontFamily: "monospace", color: "#fff", lineHeight: 1, marginBottom: 12 }}>
            {tokenNum(serving)}
          </div>
          <div style={{ marginBottom: 12 }}>
            {(serving.items || []).map((item, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#ccc", padding: "3px 0" }}>
                <span>× {item.qty} {item.name}</span>
                <span style={{ color: "#fff", fontWeight: 600 }}>₹{item.price * item.qty}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: "1px solid #1a4a1a", fontSize: 16, fontWeight: 800, color: "#fff" }}>
              <span>Total</span>
              <span>₹{orderTotal(serving).toLocaleString("en-IN")}</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => onUpdateStatus(serving.orderId, "done")}
              style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: "#22c55e", color: "#000", fontSize: 15, fontWeight: 700, cursor: "pointer" }}
            >
              ✓ Done
            </button>
            <button
              onClick={() => onGenerateInvoice(serving)}
              style={{ padding: "12px 16px", borderRadius: 12, border: "1px solid #22c55e", background: "transparent", color: "#22c55e", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
            >
              🧾
            </button>
            <button
              onClick={() => onUpdateStatus(serving.orderId, "pending")}
              style={{ padding: "12px 14px", borderRadius: 12, border: "1px solid #444", background: "transparent", color: "#888", fontSize: 13, cursor: "pointer" }}
            >
              ↩
            </button>
          </div>
        </div>
      )}

      {/* BIG NEXT button */}
      <button
        onClick={() => next && onUpdateStatus(next.orderId, "preparing")}
        disabled={!next || !!serving}
        style={{
          width: "100%",
          background: next && !serving
            ? "linear-gradient(135deg, #FF6B35 0%, #f5c542 100%)"
            : "#e5e5e5",
          color: next && !serving ? "#fff" : "#aaa",
          border: "none",
          borderRadius: 20,
          padding: "28px 20px",
          fontSize: 48,
          fontWeight: 900,
          letterSpacing: 4,
          cursor: next && !serving ? "pointer" : "not-allowed",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          marginBottom: 20,
          lineHeight: 1,
          boxShadow: next && !serving ? "0 8px 32px rgba(255,107,53,0.3)" : "none",
          transition: "all 0.2s",
        }}
      >
        NEXT
        <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: 0, opacity: 0.85 }}>
          {serving
            ? "Finish current order first"
            : next
              ? `Token ${tokenNum(next)} · ₹${orderTotal(next)}`
              : "No orders waiting"}
        </span>
      </button>

      {/* Waiting queue list */}
      {pending.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: "#aaa", fontWeight: 700, marginBottom: 10 }}>
            WAITING ({pending.length})
          </div>
          {pending.map((o, i) => (
            <div key={o.orderId} style={{
              background: i === 0 ? "#fff8f5" : "#fff",
              border: `1.5px solid ${i === 0 ? "#FF6B35" : "#efefed"}`,
              borderRadius: 12, padding: "12px 14px", marginBottom: 8,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{ fontSize: 28, fontWeight: 900, fontFamily: "monospace", color: i === 0 ? "#FF6B35" : "#888", minWidth: 56 }}>
                {tokenNum(o)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: "#444" }}>
                  {(o.items || []).map(item => `${item.name} ×${item.qty}`).join(", ")}
                </div>
              </div>
              <div style={{ fontWeight: 700, color: "#111", fontSize: 14 }}>
                ₹{orderTotal(o).toLocaleString("en-IN")}
              </div>
              {i === 0 && (
                <div style={{ fontSize: 11, color: "#FF6B35", fontWeight: 700 }}>NEXT</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Done orders — at bottom */}
      {done.length > 0 && (
        <div>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: "#aaa", fontWeight: 700, marginBottom: 10 }}>
            DONE ({done.length})
          </div>
          {done.map(o => (
            <div key={o.orderId} style={{
              background: "#fff", border: "1px solid #efefed",
              borderRadius: 12, padding: "12px 14px", marginBottom: 8,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{ fontSize: 28, fontWeight: 900, fontFamily: "monospace", color: "#22c55e", minWidth: 56 }}>
                {tokenNum(o)}
              </div>
              <div style={{ flex: 1, fontSize: 13, color: "#888" }}>
                {(o.items || []).map(i => `${i.name} ×${i.qty}`).join(", ")}
              </div>
              <div style={{ fontWeight: 700, color: "#111", fontSize: 14 }}>₹{orderTotal(o)}</div>
              <button
                onClick={() => onGenerateInvoice(o)}
                style={{ padding: "7px 12px", borderRadius: 10, border: "1px solid #6366f1", background: "#fff", color: "#6366f1", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                🧾
              </button>
              <button
                onClick={() => onUpdateStatus(o.orderId, "billed")}
                style={{ padding: "7px 12px", borderRadius: 10, border: "none", background: "#6366f1", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
              >
                💰
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrdersList({ orders, ordersMode, onUpdateStatus, onGenerateInvoice, category }) {
  let lastDate = null;
  return (
    <>
      {orders.map(order => {
        const orderDate = new Date(order.createdAt).toLocaleDateString("en-IN", {
          day: "numeric", month: "short", year: "numeric"
        });
        const showDateHeader = ordersMode === "history" && orderDate !== lastDate;
        lastDate = orderDate;
        const borderColor = order.status === "pending" ? "#FF6B35" : order.status === "preparing" ? "#f5c542" : order.status === "done" ? "#22c55e" : order.status === "cancelled" ? "#fca5a5" : "#6366f1";
        const headerBg = order.status === "pending" ? "#fff5f2" : order.status === "preparing" ? "#fffbeb" : order.status === "done" ? "#f0fdf4" : order.status === "cancelled" ? "#fff5f5" : "#f5f3ff";
        const statusColor = order.status === "pending" ? "#FF6B35" : order.status === "preparing" ? "#d97706" : order.status === "done" ? "#16a34a" : order.status === "cancelled" ? "#dc2626" : "#6366f1";
        const statusLabel = order.status === "pending" ? "🔴 New" : order.status === "preparing" ? "🟡 Preparing" : order.status === "done" ? "🟢 Done" : order.status === "cancelled" ? "✕ Cancelled" : "💰 Billed";
        const orderTotal = (order.items || []).reduce((t, i) => t + i.price * i.qty, 0);

        return (
          <div key={order.orderId}>
            {showDateHeader && (
              <div style={{
                margin: "4px 16px 8px",
                fontSize: 11, fontWeight: 700,
                color: "#aaa", textTransform: "uppercase", letterSpacing: 1,
              }}>
                {orderDate}
              </div>
            )}
            <div style={{
              background: "#fff", margin: "0 16px 12px",
              borderRadius: 14, border: `2px solid ${borderColor}`,
              overflow: "hidden",
            }}>
              {/* Header */}
              <div style={{
                padding: "10px 14px", background: headerBg,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: statusColor, textTransform: "uppercase", letterSpacing: 0.5 }}>{statusLabel}</span>
                  {order.tokenNumber && <div style={{ fontSize: 36, fontWeight: 900, color: "#111", letterSpacing: 2, padding: "8px 14px 4px", fontFamily: "monospace" }}>{order.tokenNumber}</div>}
                  <span style={{ fontSize: 12, color: "#888" }}>·</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#444" }}>
                    {order.tableLabel || (["Restaurant", "Bakery", "Sweet Shop", "Dhaba"].includes(category) ? "Dine-in" : "Walk-in")}
                  </span>
                  {order.customerPhone && (
                    <a href={`tel:${order.customerPhone}`} style={{ fontSize: 12, color: "#6366f1", fontWeight: 600, textDecoration: "none" }}>
                      📱 {order.customerPhone}
                    </a>
                  )}
                </div>
                <span style={{ fontSize: 11, color: "#aaa" }}>{timeAgo(order.createdAt)}</span>
              </div>

              {/* Items */}
              <div style={{ padding: "10px 14px" }}>
                {(order.items || []).map((item, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between",
                    fontSize: 14, padding: "3px 0",
                    borderBottom: i < order.items.length - 1 ? "1px solid #f5f5f3" : "none",
                  }}>
                    <span style={{ color: "#333" }}>
                      <span style={{ fontWeight: 600, marginRight: 6 }}>×{item.qty}</span>
                      {item.name}
                    </span>
                    <span style={{ fontWeight: 600, color: "#111" }}>
                      ₹{(item.price * item.qty).toLocaleString("en-IN")}
                    </span>
                  </div>
                ))}

                {order.customerNote && (
                  <div style={{
                    marginTop: 8, padding: "6px 10px",
                    background: "#f9f9f9", borderRadius: 8,
                    fontSize: 12, color: "#666", fontStyle: "italic",
                  }}>
                    📝 {order.customerNote}
                  </div>
                )}

                <div style={{
                  display: "flex", justifyContent: "space-between",
                  marginTop: 8, paddingTop: 8,
                  borderTop: "1px solid #f0f0f0",
                  fontWeight: 700, fontSize: 15,
                }}>
                  <span>Total</span>
                  <span>₹{orderTotal.toLocaleString("en-IN")}</span>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ padding: "0 14px 12px", display: "flex", gap: 8 }}>
                {order.status === "pending" && (
                  <>
                    <button
                      onClick={() => onUpdateStatus(order.orderId, "preparing")}
                      style={{ flex: 1, padding: "9px", borderRadius: 10, border: "none", background: "#111", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                    >
                      Start Preparing
                    </button>
                    <button
                      onClick={() => { if (window.confirm("Cancel this order?")) onUpdateStatus(order.orderId, "cancelled"); }}
                      style={{ padding: "9px 14px", borderRadius: 10, border: "1.5px solid #fca5a5", background: "#fff0f0", color: "#dc2626", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                    >
                      ✕ Cancel
                    </button>
                  </>
                )}
                {order.status === "preparing" && (
                  <>
                    <button
                      onClick={() => onUpdateStatus(order.orderId, "done")}
                      style={{ flex: 1, padding: "9px", borderRadius: 10, border: "none", background: "#22c55e", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                    >
                      ✓ Mark as Done
                    </button>
                    <button
                      onClick={() => onUpdateStatus(order.orderId, "pending")}
                      style={{ padding: "9px 14px", borderRadius: 10, border: "1.5px solid #ddd", background: "#fff", color: "#888", fontSize: 13, cursor: "pointer" }}
                    >
                      ↩ Undo
                    </button>
                  </>
                )}
                {order.status === "done" && (
                  <>
                    <button
                      onClick={() => onUpdateStatus(order.orderId, "billed")}
                      style={{ flex: 1, padding: "9px", borderRadius: 10, border: "none", background: "#6366f1", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                    >
                      💰 Bill Cleared
                    </button>
                    <button
                      onClick={() => onUpdateStatus(order.orderId, "preparing")}
                      style={{ padding: "9px 14px", borderRadius: 10, border: "1.5px solid #ddd", background: "#fff", color: "#888", fontSize: 13, cursor: "pointer" }}
                    >
                      ↩ Undo
                    </button>
                  </>
                )}
                {order.status === "billed" && (
                  <button
                    onClick={() => onGenerateInvoice(order)}
                    style={{ flex: 1, padding: "9px", borderRadius: 10, border: "1.5px solid #6366f1", background: "#fff", color: "#6366f1", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  >
                    🧾 Invoice
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
function timeAgo(isoString) {
  const diff = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}









